




















































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import SegmentControl from  "@share/common/segment/Segment.vue";
import { SegmentDataItem } from "@share/common/segment/SegmentData";
import SessionMainInfo from "./SessionMainInfo.vue";
import SessionCounters from "./SessionCounters.vue";

import {Element} from "@api/models/Element";
import Comments from "@share/comments/components/Comments.vue";
import UsersList from "@share/common/users-list/UsersList.vue";
import { ProgramDataHelper } from "@share/program/helpers/ProgramDataHelper";
import { DateTime } from "luxon";
import { Debounce } from "vue-debounce-decorator";
import { secondsBeforeMoment } from "@common/webinarHelper";
import { mayAttendToSession } from "@common/AgendaHelper";
import { BackgroundViewHelper } from "@service/components/background-view/BackgroundViewHelper/BackgroundViewHelper";
import { BackgroundViewName } from "@service/components/background-view/types/BackgroundViewName";
import { OnlineProvider } from "@api/enums/Session/OnlineProviderEnum";
import {Rating} from "@api/models/Rating";
import { UserProfile } from "@api/models/UserProfile";
import AttendButton from "./AttendButton.vue"

const SegmentNames = {
    info: "Info",
    chat: "Chat"
}

@Component({
    components: { SegmentControl, SessionCounters, Comments, UsersList, SessionMainInfo, AttendButton }
})
export default class SessionListInfo extends Vue {

    @Prop({ required: true }) session: Element;
    @Prop({ default: false }) simpleMode: boolean;
    @Prop({ default: null }) speakers: UserProfile[] | null;
    @Prop({default: null}) likesProp: Record<string, Rating> | null;
    @Prop({default: null}) commentsProp: Record<string, number> | null;

    @Prop({ default: false }) useSelectedTimeZone: boolean;

    activeSegment: string = SegmentNames.info;
    alreadyScheduled = false;
    currentSessionId = "";

    schedule() {
        this.alreadyScheduled = true;
    }

    setCurrentPartView(value: string) {
        this.activeSegment = value;
    }

    async goToSession() {

        const confirmed = await BackgroundViewHelper.checkBroadcast(this.$store, this.session.id, BackgroundViewName.session);

        if(confirmed) {
            this.$emit('select');
            this.$router.push({ name: 'ViewSession', params: { ...this.$route.params, id: this.session.id} })
        }
    }

    likeChanged(value: number|null) {
        this.programDataHelper?.updateLike(this.session.id, value);

        this.setLikes(value);
    }

    @Debounce(500)
    setLikes(value: number|null) {
        this.programDataHelper?.toggleAgendaLike(this.session.id, value, false);
    }

    @Watch('session')
    watchSession(session: Element) {
        if(this.currentSessionId !== session.id) {
            this.activeSegment = SegmentNames.info;
            this.currentSessionId = session.id;
        }

    }

    onNewComment() {
        if (typeof this.programDataHelper?.comments[this.session.id] === "number") {
            this.programDataHelper.comments[this.session.id]++;
        }
    }

    checkIsOnlineParam(session: Element) {
        const now = DateTime.local();
        return session?.beginAt <= now && session?.endAt >= now;
    }

    get showFooter() {
        return this.simpleMode && this.showAttend
    }

    get isPastSession() {
        return this.session.onlineProvider === OnlineProvider.pastSessionRecord
    }

    get showVideo() {
        const now = DateTime.local();
        const ended = this.session?.beginAt < now && this.session?.endAt < now;
        return ended && this.isCustom;
    }

    get isCustom() {
        return Boolean(this.session?.onlineParams?.custom?.url);
    }

    get likes() {
        return this.likesProp != null ? (this.likesProp[this.session.id] ?? {}) : this.programDataHelper?.likes[this.session.id]
    }

    get customUrl() {
        return this.session?.onlineParams?.custom?.url;
    }

    get isEnded() {
        return secondsBeforeMoment(this.session.endAt) < 0;
    }

    get commentsCount() {
        return this.commentsProp != null ? (this.commentsProp[this.session.id] ?? 0) : (this.programDataHelper?.comments?.[this.session.id] || 0);
    }

    get description() {
        return this.session.description || "";
    }

    get alreadyOpened() {
        return this.$route.name === "ViewSession" && this.$route.params?.id === this.session.id;
    }

    /**
     * Data helper for data manipulation, comes from vuex store by date
     */
    get programDataHelper(): ProgramDataHelper | null {
        if (this.date) {
            const dateStr = this.date?.toISODate();
            return this.$store.state.program.programs[dateStr] || {};
        }
        return null;
    }

    get date(): DateTime | null {
        return this.$store.state.program.currentDate;
    }

    get segmentData(): SegmentDataItem[] {
        return [{
            active: this.showInfo,
            title: this.$t(`program.${SegmentNames.info.toLowerCase()}`).toString(),
            value: SegmentNames.info
        },
        {
            active: this.showComments,
            title: this.$t(`common.${SegmentNames.chat.toLowerCase()}`).toString(),
            value: SegmentNames.chat
        }];
    }

    get showInfo() {
        return this.activeSegment === SegmentNames.info
    }

    get showAttend() {
        return mayAttendToSession(this.session, (this.isAdmin || this.isSpeaker), this.alreadyOpened);
    }

    get showComments() {
        return this.activeSegment === SegmentNames.chat
    }

    get isAdmin() {
      return this.$store.getters['isAdmin'];
    }

    get isSpeaker() {
      return this.$store.getters['isSpeaker'];
    }

    get isManualControlled() {
        return this.session.isManualControlled
    }
}
